/**
 * Where a room's tables stand, how big each is drawn, and the tidy layout a
 * room nobody has arranged starts from. Apart from `room-layout.tsx` so that
 * file exports only components, which is what fast refresh needs.
 */

import { useMoveTable, type FloorTable } from '@restaurant/api-client';

export type Point = { x: number; y: number };

/** Plan units snapped to, so tables dropped by a thumb still line up. */
export const SNAP = 10;
/** Room left around each table for its chairs, in plan units. */
export const CHAIR_ROOM = 16;
/** The narrowest plan drawn, so two tables do not fill a whole screen. */
export const MIN_PLAN_WIDTH = 900;
/**
 * How small and large the plan may be drawn, per plan unit. Below the floor a
 * two-top stops being something a thumb finds; above the ceiling a room of
 * three tables fills a tablet with furniture. A room wider than the screen at
 * the floor scrolls sideways rather than shrinking further.
 */
export const MIN_SCALE = 0.9;
export const MAX_SCALE = 1.5;
/** The width the starting layout wraps at, in plan units. */
const START_ROW = 1000;
const START_GAP = 40;

/** The table as drawn, without its chairs: sized by what it seats. */
export function tableSize(table: FloorTable): { w: number; h: number; round: boolean } {
  const round = table.shape === 'round';
  const seats = table.seats;
  // A stored size wins when somebody has set one; 80 × 80 is the default the
  // table was created with, which says nothing about the furniture.
  const stored = { w: Number(table.width), h: Number(table.height) };
  if (stored.w !== 80 || stored.h !== 80) return { ...stored, round };
  if (round) {
    const d = seats <= 2 ? 70 : seats <= 4 ? 90 : seats <= 6 ? 110 : 130;
    return { w: d, h: d, round };
  }
  if (seats <= 2) return { w: 70, h: 70, round };
  if (seats <= 4) return { w: 90, h: 90, round };
  if (seats <= 6) return { w: 130, h: 90, round };
  return { w: 170, h: 90, round };
}

/** The table and the chairs around it: what takes up floor. */
export function spotSize(table: FloorTable): { w: number; h: number } {
  const { w, h } = tableSize(table);
  return { w: w + CHAIR_ROOM * 2, h: h + CHAIR_ROOM * 2 };
}

/** Whether anybody has arranged this room, or it is still every table at 0,0. */
export function isArranged(tables: FloorTable[]): boolean {
  return tables.some((t) => Number(t.pos_x) !== 0 || Number(t.pos_y) !== 0);
}

/** Rows in label order, each table given its own width, wrapping at a room's width. */
export function startingPositions(tables: FloorTable[]): Map<string, Point> {
  const sorted = [...tables].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true }),
  );
  const out = new Map<string, Point>();
  let x = 20;
  let y = 20;
  let rowHeight = 0;
  for (const table of sorted) {
    const spot = spotSize(table);
    if (x > 20 && x + spot.w > START_ROW) {
      x = 20;
      y += rowHeight + START_GAP;
      rowHeight = 0;
    }
    out.set(table.id, { x, y });
    x += spot.w + START_GAP;
    rowHeight = Math.max(rowHeight, spot.h);
  }
  return out;
}

export function positionsOf(tables: FloorTable[]): Map<string, Point> {
  if (!isArranged(tables)) return startingPositions(tables);
  return new Map(tables.map((t) => [t.id, { x: Number(t.pos_x), y: Number(t.pos_y) }]));
}

/**
 * Save the tidy starting layout for a room nobody has arranged, so what the
 * arranging screen shows is what is stored even if nothing is then dragged.
 */
export function useLayOutRoom() {
  const move = useMoveTable();
  return {
    pending: move.isPending,
    error: move.error,
    /**
     * Returns the positions it saved, for the screen to start from: the room
     * as read from the server may not have caught up with every one yet.
     */
    layOut: async (tables: FloorTable[]): Promise<Map<string, Point> | null> => {
      if (isArranged(tables)) return null;
      const start = startingPositions(tables);
      for (const [id, at] of start) {
        await move.mutateAsync({ tableId: id, posX: at.x, posY: at.y });
      }
      return start;
    },
  };
}
